"use client";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import styles from "./AdminSidebar.module.css";

const hasPermission = (requiredRoles, userRole) => {
  return requiredRoles.includes(userRole);
};

export default function AdminSidebar({
  userRole = null,
  isSidebarOpen,
  toggleSidebar,
}) {
  const [activeMenu, setActiveMenu] = useState(null);
  const location = useLocation(); // Move useLocation to the component level

  // Modified toggle function that closes other menus when opening a new one
  const toggleMenu = (menuId) => {
    setActiveMenu(activeMenu === menuId ? null : menuId);
  };

  // Check if any item in a submenu is active
  const isItemActive = (items) => {
    return items.some((item) => location.pathname === item.to);
  };

  const navItems = [
    {
      type: "item",
      to: "/xl-admin",
      icon: <i className="bi bi-house me-2"></i>,
      label: "Home",
      roles: ["admin", "editor", "superadmin"],
    },
    {
      type: "submenu",
      id: "QuesMenu",
      icon: <i className="bi bi-question-circle me-2"></i>,
      label: "Questions",
      roles: ["admin", "editor", "superadmin"],
      items: [
        { to: "/xl-admin/questions", label: "All Questions" },
        { to: "/xl-admin/add-question", label: "Add New" },
      ],
    },
    {
      type: "submenu",
      id: "PostsMenu",
      icon: <i className="bi bi-book-half me-2"></i>,
      label: "Lessons",
      roles: ["admin", "editor", "superadmin"],
      items: [
        { to: "/xl-admin/lessons", label: "All Lessons" },
        { to: "/xl-admin/add-lesson", label: "Add New" },
      ],
    },
    {
      type: "submenu",
      id: "formsMenu",
      icon: <i className="bi bi-tags me-2"></i>,
      label: "Categories",
      roles: ["admin", "editor", "superadmin"],
      items: [
        { to: "/xl-admin/categories", label: "All Categories" },
        { to: "/xl-admin/add-category", label: "Add New" },
      ],
    },
    {
      type: "submenu",
      id: "PagesMenu",
      icon: <i className="bi bi-file-text me-2"></i>,
      label: "Pages",
      roles: ["admin", "superadmin"],
      items: [
        { to: "/xl-admin/pages", label: "All Pages" },
        { to: "/xl-admin/add-page", label: "Add New" },
      ],
    },
    {
      type: "submenu",
      id: "AppearanceMenu",
      icon: <i className="bi bi-brush me-2"></i>,
      label: "Appearance",
      roles: ["admin", "superadmin"],
      items: [
        { to: "/xl-admin/cards", label: "Cards" },
        { to: "/xl-admin/slides", label: "Slides" },
        { to: "/xl-admin/customer-reviews", label: "Customer Reviews" },
      ],
    },
    {
      type: "submenu",
      id: "SettingsMenu",
      icon: <i className="bi bi-gear me-2"></i>,
      label: "Settings",
      roles: ["admin", "superadmin"],
      items: [
        { to: "/xl-admin/options-general", label: "General" },
        { to: "/xl-admin/pricing", label: "Pricing" },
        { to: "/xl-admin/coupons", label: "Coupons" },
        { to: "/xl-admin/actcodes", label: "Create Codes" },
        { to: "/xl-admin/acodes", label: "Codes" },
      ],
    },
    {
      type: "item",
      to: "/xl-admin/users",
      icon: <i className="bi bi-people me-2"></i>,
      label: "Users",
      roles: ["admin", "superadmin"],
    },
    {
      type: "submenu",
      id: "AffiliatesMenu",
      icon: <i className="bi bi-person-fill-add me-2"></i>,
      label: "Affiliates",
      roles: ["superadmin"],
      items: [
        { to: "/xl-admin/affiliate-general", label: "General" },
        { to: "/xl-admin/applications", label: "Applications" },
        { to: "/xl-admin/affiliates", label: "Affiliates" },
      ],
    },
    {
      type: "item",
      to: "/xl-admin/orders",
      icon: <i className="bi bi-receipt me-2"></i>,
      label: "Orders",
      roles: ["admin", "editor", "superadmin"],
    },
    {
      type: "item",
      to: "/xl-admin/logout",
      icon: <i className="bi bi-power me-2"></i>,
      label: "Logout",
      roles: ["admin", "editor", "superadmin"],
    },
  ];

  return (
    <nav
      id="sidebar"
      className={`${styles.adminSidebar} text-white p-3 h-100 bg-lime-green ${
        isSidebarOpen ? "" : styles.active
      }`}
    >
      <div className={styles.customMenu}>
        <button
          type="button"
          onClick={toggleSidebar}
          className={`btn btn-primary ${styles.aBtn}`}
        >
          <i className="bi bi-list"></i>
          <span className="visually-hidden">Toggle Menu</span>
        </button>
      </div>

      {/* Fixed header section */}
      <div style={{ height: "auto" }} className="text-center">
        <Link
          to="/"
          className="mb-3 mb-md-0 me-md-auto text-white text-decoration-none"
        >
          <span className="fs-4">XL in Maths</span>
        </Link>
        <hr />
      </div>

      {/* Scrollable navigation section */}
      <div
        className="overflow-auto"
        style={{
          height: "calc(100vh - 100px)",
          paddingBottom: "20px",
        }}
      >
        <ul className="nav nav-pills flex-column mb-auto">
          {navItems.map((item) => {
            if (!hasPermission(item.roles, userRole)) return null;

            if (item.type === "item") {
              return (
                <NavItem
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                />
              );
            } else if (item.type === "submenu") {
              return (
                <Submenu
                  key={item.id}
                  id={item.id}
                  icon={item.icon}
                  label={item.label}
                  items={item.items}
                  isActive={activeMenu === item.id || isItemActive(item.items)}
                  toggleMenu={() => toggleMenu(item.id)}
                />
              );
            }
            return null;
          })}
        </ul>
      </div>
    </nav>
  );
}

function NavItem({ to, icon, label }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <li className="nav-item">
      <Link
        to={to}
        className={`nav-link d-flex align-items-center ${
          isActive ? "bg-inchworm text-dark rounded" : "text-white"
        }`}
      >
        {icon}
        {label}
      </Link>
    </li>
  );
}

function Submenu({ id, icon, label, items, isActive, toggleMenu }) {
  const location = useLocation();

  return (
    <li className="nav-item mb-1" key={id}>
      <button
        className="nav-link text-white d-flex justify-content-between align-items-center w-100"
        onClick={toggleMenu}
        aria-expanded={isActive}
      >
        <span>
          {icon}
          {label}
        </span>
        <i
          className={`bi bi-chevron-down transition-transform ${
            isActive ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      <div className={`collapse ${isActive ? "show" : ""} ps-4`}>
        <ul className="btn-toggle-nav list-unstyled fw-normal pb-1 small">
          {items.map((item, index) => {
            const isActiveChild = location.pathname === item.to;
            return (
              <li key={index}>
                <Link
                  to={item.to}
                  className={`nav-link rounded ${
                    isActiveChild ? "bg-inchworm text-dark" : "text-white"
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
}
